import { cn } from '@/lib/utils/cn'

export type MetricValueTone = 'default' | 'success' | 'warning' | 'danger'
export type MetricDeltaTone = 'neutral' | 'positive' | 'negative'
export type MetricCardVariant = 'card' | 'inline'

export interface MetricCardProps extends React.HTMLAttributes<HTMLDivElement> {
  label: string
  value: React.ReactNode
  /** Optional sub-metric, e.g. "YoY +12%". */
  delta?: React.ReactNode
  deltaTone?: MetricDeltaTone
  /** Paint value in a semantic color (e.g. overdue counts). Default: primary. */
  valueTone?: MetricValueTone
  /**
   * `card` (default) — standalone card with border + padding, 3xl value.
   * `inline` — compact, no border/padding, 2xl value. Designed to sit inside
   * a single page-header band next to the title (see MetricStrip variant="inline").
   */
  variant?: MetricCardVariant
}

const valueToneClass: Record<MetricValueTone, string> = {
  default: 'text-primary',
  success: 'text-success-text',
  warning: 'text-warning-text',
  danger: 'text-danger-text',
}

const deltaToneClass: Record<MetricDeltaTone, string> = {
  neutral: 'text-secondary',
  positive: 'text-success-text',
  negative: 'text-danger-text',
}

/**
 * Single-metric card. No decorative borders or top accents (Principle 2.1).
 * Color appears only on genuinely semantic values (overdue, deltas).
 */
export function MetricCard({
  label,
  value,
  delta,
  deltaTone = 'neutral',
  valueTone = 'default',
  variant = 'card',
  className,
  ...rest
}: MetricCardProps) {
  const isInline = variant === 'inline'
  return (
    <div
      className={cn(
        'flex flex-col gap-0.5',
        isInline
          ? 'px-4'
          : 'min-w-[var(--metric-card-min-width)] gap-1 rounded-md border border-border-subtle bg-surface px-4 py-3',
        className,
      )}
      {...rest}
    >
      <span className={cn('text-role-kpi-label', isInline && 'whitespace-nowrap')}>{label}</span>
      <span
        className={cn(
          'font-semibold leading-[1.1] tabular-nums tracking-tight',
          isInline ? 'text-2xl' : 'text-3xl',
          valueToneClass[valueTone],
        )}
      >
        {value}
      </span>
      {delta !== undefined ? (
        <span className={cn('font-sans text-xs font-medium tabular-nums', deltaToneClass[deltaTone])}>
          {delta}
        </span>
      ) : null}
    </div>
  )
}

/**
 * Horizontal strip of metric cards. Spec §DataView: 4–6 KPIs in one row,
 * 120px vertical — only when the metrics tell a different story than the
 * table itself (Principle 2.3).
 *
 * `variant="inline"` switches to a flex layout with divider rules between
 * cards, for use inside a single page-header band next to the title.
 */
export function MetricStrip({
  children,
  variant = 'card',
  className,
}: {
  children: React.ReactNode
  variant?: MetricCardVariant
  className?: string
}) {
  if (variant === 'inline') {
    return (
      <div className={cn('flex items-center divide-x divide-border-subtle overflow-x-auto', className)}>
        {children}
      </div>
    )
  }
  return (
    <div
      className={cn('grid gap-2', className)}
      style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(var(--metric-card-min-width), 1fr))' }}
    >
      {children}
    </div>
  )
}
